import axios from 'axios';
const adminURL = process.env.VUE_APP_ADMIN_URL;

function errorResult(e) {
  const data = e?.response?.data ?? {};
  // `missing` comes with MISSING_VIEW_PERMISSION (spec 132): the required tags not sent.
  return {
    status: 'error',
    code: data.code ?? null,
    message: data.message ?? null,
    missing: Array.isArray(data.missing) ? data.missing : []
  };
}

export default class EjecutivosService {
  static getHeaders() {
    const token = JSON.parse(localStorage.getItem('accessToken'));
    return { Authorization: `Bearer ${token}` };
  }

  static async getAll(all = false) {
    try {
      const params = all ? '?all=true' : '';
      const res = await axios.get(`${adminURL}/useradmin${params}`, { headers: this.getHeaders() });
      return { status: 'ok', data: res.data.data };
    } catch (e) { return { status: 'error' }; }
  }

  static async toggleStatus(id) {
    try {
      await axios.patch(`${adminURL}/useradmin/${id}/status`, {}, { headers: this.getHeaders() });
      return { status: 'ok' };
    } catch (e) {
      // `code` lets the view explain LAST_PERMISSION_HOLDER (spec 131).
      return errorResult(e);
    }
  }

  /**
   * Creates an administrator. The backend generates the password, emails it,
   * and derives `createdBy` from the caller's session — the client never sends it.
   * `permissions` (spec 131) is the initial tag list; the backend defaults to `[]`.
   */
  static async create({ name, surname, email, rut, permissions }) {
    try {
      const body = { name, surname, email, rut };
      if (Array.isArray(permissions)) body.permissions = permissions;
      await axios.post(`${adminURL}/useradmin`, body, { headers: this.getHeaders() });
      return { status: 'ok' };
    } catch (e) {
      return errorResult(e);
    }
  }

  /**
   * Edits an administrator (`admins:update`). Send only the changed fields.
   * @param {string} id
   * @param {{ name?: string, surname?: string, rut?: string, email?: string }} fields
   * @returns {Promise<{ status: 'ok', data: object } | { status: 'error', code: string|null, message: string|null }>}
   */
  static async update(id, fields) {
    try {
      const res = await axios.patch(`${adminURL}/useradmin/${id}`, fields, {
        headers: this.getHeaders()
      });
      return { status: 'ok', data: res.data.data };
    } catch (e) {
      return errorResult(e);
    }
  }

  /**
   * Permission tag catalog, sections and profiles (specs 131/132).
   * `sections` (spec 132) groups the tags by menu leaf:
   * `[{ key, label, group, actions: [{ tag, label, kind, requires }] }]`.
   * @returns {Promise<{ status: 'ok', data: { permissions: { tag: string, label: string, kind?: string, requires?: string|null }[], sections?: object[], profiles: { key: string, label: string, permissions: string[] }[] } } | { status: 'error' }>}
   */
  static async getPermissionsCatalog() {
    try {
      const res = await axios.get(`${adminURL}/useradmin/permissions/catalog`, {
        headers: this.getHeaders()
      });
      return { status: 'ok', data: res.data.data };
    } catch (e) {
      return errorResult(e);
    }
  }

  /**
   * Replaces the full tag list of another administrator (spec 131).
   * @param {string} id
   * @param {string[]} permissions
   * @returns {Promise<{ status: 'ok', data: { id: string, permissions: string[] } } | { status: 'error', code: string|null, message: string|null }>}
   */
  static async updatePermissions(id, permissions) {
    try {
      const res = await axios.put(
        `${adminURL}/useradmin/${id}/permissions`,
        { permissions },
        { headers: this.getHeaders() }
      );
      return { status: 'ok', data: res.data.data };
    } catch (e) {
      return errorResult(e);
    }
  }
}
